// Apolo IDE — AppearanceView (design-uplift E3).
//
// Cursor-parity "Customize / Appearance" — the first unified place to tune how
// the app looks. Native <dialog> + framer-motion (mirrors PluginsView's modal
// contract). Three sections, all wired to EXISTING persistence:
//   • Tema       — light / dark / system (useTheme)
//   • Destaque   — accent hue + intensity sliders + reset (useTheme accent; E2)
//   • Densidade  — chat density compact / comfortable / detailed (chatDensity; E1)
// Zero hex literals — swatch reads var(--apolo-accent), which retints live.

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, X, Sun, Moon, Monitor, RotateCcw } from 'lucide-react';
import { THEMES, useTheme } from '../hooks/useTheme.js';
import { isDefaultAccent } from '../lib/accentTheme.js';
import { isComputerUseEnabled, setComputerUseEnabled } from '../lib/computerUseTool.js';
import {
  getDensity,
  setDensity as persistDensity,
  DENSITIES,
  DENSITY_LABELS,
  normalizeDensity,
  CHAT_DENSITY_EVENT,
} from '../lib/chatDensity.js';

const SPRING = { type: 'spring', stiffness: 320, damping: 28 };

function Segmented({ ariaLabel, options, value, onChange }) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-0.5">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 ${
              active ? 'bg-apolo-accent text-white' : 'text-apolo-textSecondary hover:bg-apolo-surface1 hover:text-apolo-textPrimary'
            }`}
          >
            {opt.Icon ? <opt.Icon size={13} aria-hidden="true" /> : null}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="flex flex-col gap-2 border-b border-apolo-borderSubtle py-4 last:border-b-0">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-apolo-textPrimary">{label}</span>
        {hint ? <span className="text-xs text-apolo-textTertiary">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

function ThemeSection() {
  const { theme, setTheme } = useTheme();
  return (
    <Field label="Tema" hint="Claro, escuro, ou seguir o sistema.">
      <Segmented
        ariaLabel="Tema"
        value={theme}
        onChange={setTheme}
        options={[
          { value: THEMES.LIGHT, label: 'Claro', Icon: Sun },
          { value: THEMES.DARK, label: 'Escuro', Icon: Moon },
          { value: THEMES.SYSTEM, label: 'Sistema', Icon: Monitor },
        ]}
      />
    </Field>
  );
}

function AccentSection() {
  const { accent, setAccent, resetAccent } = useTheme();
  const isDefault = isDefaultAccent(accent);
  const hueRef = useRef(null);
  // Reset disables itself (back to default) → the focused button would blur to
  // <body>, escaping the dialog. Move focus to the hue slider first (a11y).
  const onReset = () => { resetAccent(); hueRef.current?.focus(); };
  return (
    <Field label="Cor de destaque" hint="Matiz + intensidade do acento. 0% = neutro (cinza).">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="h-8 w-8 shrink-0 rounded-full border border-apolo-borderSubtle" style={{ background: 'var(--apolo-accent)' }} />
        <div className="flex flex-1 flex-col gap-2">
          <label className="flex items-center gap-2 text-xs text-apolo-textTertiary">
            <span className="w-16 shrink-0">Matiz</span>
            <input
              ref={hueRef}
              type="range" min="0" max="359" value={accent.hue} aria-label="Matiz do destaque"
              onChange={(e) => setAccent({ ...accent, hue: Number(e.target.value) })}
              className="h-1.5 flex-1 cursor-pointer accent-apolo-accent"
            />
            <span className="w-8 shrink-0 text-right tabular-nums">{accent.hue}°</span>
          </label>
          <label className="flex items-center gap-2 text-xs text-apolo-textTertiary">
            <span className="w-16 shrink-0">Intensidade</span>
            <input
              type="range" min="0" max="100" value={accent.intensity} aria-label="Intensidade do destaque"
              onChange={(e) => setAccent({ ...accent, intensity: Number(e.target.value) })}
              className="h-1.5 flex-1 cursor-pointer accent-apolo-accent"
            />
            <span className="w-8 shrink-0 text-right tabular-nums">{accent.intensity}%</span>
          </label>
        </div>
        <button
          type="button"
          onClick={onReset}
          disabled={isDefault}
          title="Restaurar o azul padrão"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-apolo-borderSubtle text-apolo-textTertiary transition hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 disabled:opacity-40"
        >
          <RotateCcw size={14} aria-hidden="true" />
        </button>
      </div>
    </Field>
  );
}

function DensitySection() {
  const [density, setDensity] = useState(getDensity);
  useEffect(() => {
    function onChange(e) { setDensity(normalizeDensity(e?.detail)); }
    window.addEventListener(CHAT_DENSITY_EVENT, onChange);
    return () => window.removeEventListener(CHAT_DENSITY_EVENT, onChange);
  }, []);
  return (
    <Field label="Densidade do chat" hint="Quanto detalhe os cartões de ferramenta mostram.">
      <Segmented
        ariaLabel="Densidade do chat"
        value={density}
        onChange={(v) => setDensity(persistDensity(v))}
        options={DENSITIES.map((d) => ({ value: d, label: DENSITY_LABELS[d] }))}
      />
    </Field>
  );
}

function ComputerUseSection() {
  const [on, setOn] = useState(() => isComputerUseEnabled());
  const set = (v) => { setComputerUseEnabled(v); setOn(v); };
  return (
    <Field
      label="Controle do computador (experimental)"
      hint="Deixa o agente tirar screenshot, clicar, digitar e rodar AppleScript — com trava por app: navegadores só leem, terminais/IDEs só clicam, demais apps completo. Só no app desktop; desligado por padrão."
    >
      <Segmented
        ariaLabel="Controle do computador"
        value={on ? 'on' : 'off'}
        onChange={(v) => set(v === 'on')}
        options={[{ value: 'off', label: 'Desligado' }, { value: 'on', label: 'Ligado' }]}
      />
    </Field>
  );
}

export default function AppearanceView({ open, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (open && ref.current && !ref.current.open) ref.current.showModal();
    if (!open && ref.current && ref.current.open) ref.current.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="appearance-modal-title"
      onClose={onClose}
      className="rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1 p-0 text-apolo-textPrimary shadow-2xl backdrop:bg-apolo-overlay backdrop:backdrop-blur-sm"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ scale: 0.97, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.97, opacity: 0, y: 8 }}
            transition={SPRING}
            className="flex w-[min(28rem,100vw)] flex-col"
          >
            <header className="flex items-center justify-between border-b border-apolo-borderSubtle p-4">
              <div className="flex items-center gap-2">
                <Palette size={18} className="shrink-0 text-apolo-accent" aria-hidden="true" />
                <h2 id="appearance-modal-title" className="text-base font-semibold">Aparência</h2>
              </div>
              <button
                type="button"
                aria-label="Fechar"
                onClick={onClose}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary transition hover:text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accentRing"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </header>
            <div className="px-4 pb-4">
              <ThemeSection />
              <AccentSection />
              <DensitySection />
              <ComputerUseSection />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </dialog>
  );
}
